import { unitsLib } from 'src/app/core/services/unit-library';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { DlsOverrideModel } from 'src/app/shared/models/dlsOverride.model';
import { TrajectoryModel } from 'src/app/shared/models/trajectory.model';

export type Trajectory2dPlotView = 'northingTvd' | 'eastingTvd' | 'planView' | 'doglegMd';

export interface Trajectory2dPlotResult {
  plotData: unknown[];
  xAxisTitle: string;
  yAxisTitle: string;
  downloadPlotName: string;
  reverseAutoRange: boolean;
}

export function buildTrajectory2dPlot(params: {
  trajectoryData: TrajectoryModel[];
  selectedPlot: string;
  userUnits: UserUnitsModel | null;
  dlsOverrides: DlsOverrideModel[] | undefined;
}): Trajectory2dPlotResult | null {
  const { trajectoryData, selectedPlot, userUnits, dlsOverrides } = params;

  if (!trajectoryData || trajectoryData.length === 0) {
    return {
      plotData: [],
      xAxisTitle: '',
      yAxisTitle: '',
      downloadPlotName: '',
      reverseAutoRange: false
    };
  }

  if (selectedPlot === 'doglegMd' && dlsOverrides === undefined) {
    return null;
  }

  if (selectedPlot === 'northingTvd') {
    return {
      plotData: [{
        x: trajectoryData.map(x => x.northing),
        y: trajectoryData.map(x => x.trueVerticalDepth),
        showlegend: false
      }],
      yAxisTitle: `TVD (${userUnits?.longLengths})`,
      xAxisTitle: `Northing (${userUnits?.longLengths})`,
      downloadPlotName: 'northing_tvd_plot',
      reverseAutoRange: false
    };
  }

  if (selectedPlot === 'eastingTvd') {
    return {
      plotData: [{
        x: trajectoryData.map(x => x.easting),
        y: trajectoryData.map(x => x.trueVerticalDepth),
        showlegend: false
      }],
      yAxisTitle: `TVD (${userUnits?.longLengths})`,
      xAxisTitle: `Easting (${userUnits?.longLengths})`,
      downloadPlotName: 'easting_tvd_plot',
      reverseAutoRange: false
    };
  }

  if (selectedPlot === 'planView') {
    const maxEasting = Math.abs(Math.max(...trajectoryData.map(x => x.easting)));
    const minEasting = Math.abs(Math.min(...trajectoryData.map(x => x.easting)));
    const maxxEasting = Math.max(minEasting, maxEasting);

    const minNorthing = Math.abs(Math.min(...trajectoryData.map(x => x.northing)));
    const maxNorthing = Math.abs(Math.max(...trajectoryData.map(x => x.northing)));
    const maxxNorthing = Math.max(minNorthing, maxNorthing);

    const yAxisValue = Math.max(maxxEasting, maxxNorthing);
    const xAxisValues = maxxEasting === 0 ? maxxNorthing : maxxEasting;

    const xAxis = {
      x: [-xAxisValues - 100, xAxisValues + 100],
      y: [0, 0],
      showlegend: false,
      name: '',
      mode: 'lines',
      line: {
        color: '#783F04'
      },
    };

    const yAxis = {
      x: [0, 0],
      y: [-yAxisValue - 100, yAxisValue + 100],
      showlegend: false,
      name: '',
      mode: 'lines',
      line: {
        color: '#783F04'
      }
    };

    const trajectory = {
      x: trajectoryData.map(x => x.easting),
      y: trajectoryData.map(x => x.northing),
      showlegend: false,
      name: '',
      line: {
        color: '#1f77b4'
      }
    };

    return {
      plotData: [xAxis, yAxis, trajectory],
      yAxisTitle: 'South (-) / North (+)',
      xAxisTitle: 'West (-) / East (+)',
      reverseAutoRange: true,
      downloadPlotName: 'plan_view_plot'
    };
  }

  if (selectedPlot === 'doglegMd') {
    const trajectoryDataCopy = structuredClone(trajectoryData);

    trajectoryDataCopy.forEach((t) => {
      const dlsOverride = dlsOverrides?.find((d) => d.measuredDepth >= t.measuredDepth)?.dlsOverride;
      if (dlsOverride) {
        t.doglegSeverity += dlsOverride;
      }

      dlsOverrides?.forEach((d) => {
        const depth = d.measuredDepth;
        if (trajectoryDataCopy.findIndex((x) => x.measuredDepth === depth) === -1) {
          const dls = d.dlsOverride;
          trajectoryDataCopy.push({ measuredDepth: depth, doglegSeverity: dls, inclination: 0, azimuth: 0, trueVerticalDepth: 0, northing: 0, easting: 0 });
        }
      });
    });

    trajectoryDataCopy.sort((a, b) => a.measuredDepth - b.measuredDepth);

    const dogleg = {
      x: trajectoryDataCopy.map(x => x.doglegSeverity),
      y: trajectoryDataCopy.map(x => x.measuredDepth),
      name: 'Total Dogleg'
    };

    for (let i = dogleg.x.length - 1; i > 0; i--) {
      dogleg.x.splice(i, 0, dogleg.x[i]);
      dogleg.y.splice(i, 0, dogleg.y[i - 1]);
    }

    const plotData: unknown[] = [dogleg];

    if (dlsOverrides && dlsOverrides.length > 0) {
      const additPoints = {
        x: dlsOverrides.map(x => x.dlsOverride),
        y: dlsOverrides.map(x => x.measuredDepth),
        name: 'Additional Dogleg',
        line: {
          color: 'red',
          dash: 'dot'
        },
        width: 7
      };

      additPoints.x.splice(0, 0, dlsOverrides[0].dlsOverride);
      additPoints.y.splice(0, 0, 0);

      additPoints.x.push(0);
      additPoints.y.push(trajectoryDataCopy[trajectoryDataCopy.length - 1].measuredDepth);

      for (let x = additPoints.x.length - 1; x > 0; x--) {
        additPoints.x.splice(x, 0, additPoints.x[x]);
        additPoints.y.splice(x, 0, additPoints.y[x - 1]);
      }

      plotData.push(additPoints);
    }

    return {
      plotData,
      yAxisTitle: `MD (${userUnits?.longLengths})`,
      xAxisTitle: `Dogleg Severity (${unitsLib[userUnits?.doglegSeverity]?.symbol})`,
      downloadPlotName: 'dogleg_md_plot',
      reverseAutoRange: false
    };
  }

  return {
    plotData: [],
    xAxisTitle: '',
    yAxisTitle: '',
    downloadPlotName: '',
    reverseAutoRange: false
  };
}
